/**
 * Application Configuration
 * Centralizes environment variable access and provides type safety
 */

import packageJson from '../../package.json'

// Environment variable keys enum
export enum EnvKeys {
  GOOGLE_CLIENT_ID = 'VITE_GOOGLE_CLIENT_ID',
  API_BASE_URL = 'VITE_API_BASE_URL',
  BUILD_NUMBER = 'VITE_BUILD_NUMBER',
  BUILD_SHA = 'VITE_BUILD_SHA',
  BUILD_DATE = 'VITE_BUILD_DATE',
  APP_VERSION = 'VITE_APP_VERSION',
  PORTAL_URL = 'VITE_PORTAL_URL',
}

// Configuration keys enum for external usage
export enum ConfigKeys {
  GOOGLE_CLIENT_ID = 'googleClientId',
  API_BASE_URL = 'apiBaseUrl',
  BUILD_NUMBER = 'buildNumber',
  BUILD_SHA = 'buildSha',
  BUILD_DATE = 'buildDate',
  APP_VERSION = 'appVersion',
  PORTAL_URL = 'portalUrl',
}

// Configuration interface for type safety
interface AppConfig {
  [ConfigKeys.GOOGLE_CLIENT_ID]: string
  [ConfigKeys.API_BASE_URL]: string
  [ConfigKeys.BUILD_NUMBER]: string
  [ConfigKeys.BUILD_SHA]: string
  [ConfigKeys.BUILD_DATE]: string
  [ConfigKeys.APP_VERSION]: string
  [ConfigKeys.PORTAL_URL]: string
}

// Default values
const defaultConfig: Partial<AppConfig> = {
  [ConfigKeys.API_BASE_URL]: 'http://localhost:3000/api',
  [ConfigKeys.BUILD_NUMBER]: 'dev',
  [ConfigKeys.BUILD_SHA]: 'local',
  [ConfigKeys.BUILD_DATE]: new Date().toISOString(),
  [ConfigKeys.APP_VERSION]: packageJson.version || '0.0.0',
  [ConfigKeys.PORTAL_URL]: 'http://localhost:5174',
}

// Get environment variable with fallback
function getEnvVar(key: EnvKeys, fallback?: string): string {
  const value = import.meta.env[key]
  if (!value && !fallback) {
    throw new Error(`Missing required environment variable: ${key}`)
  }
  return value || fallback || ''
}

// Build configuration object
const config: AppConfig = {
  [ConfigKeys.GOOGLE_CLIENT_ID]: getEnvVar(EnvKeys.GOOGLE_CLIENT_ID),
  [ConfigKeys.API_BASE_URL]: getEnvVar(
    EnvKeys.API_BASE_URL,
    defaultConfig[ConfigKeys.API_BASE_URL],
  ),
  [ConfigKeys.BUILD_NUMBER]: getEnvVar(
    EnvKeys.BUILD_NUMBER,
    defaultConfig[ConfigKeys.BUILD_NUMBER],
  ),
  [ConfigKeys.BUILD_SHA]: getEnvVar(EnvKeys.BUILD_SHA, defaultConfig[ConfigKeys.BUILD_SHA]),
  [ConfigKeys.BUILD_DATE]: getEnvVar(EnvKeys.BUILD_DATE, defaultConfig[ConfigKeys.BUILD_DATE]),
  [ConfigKeys.APP_VERSION]: getEnvVar(EnvKeys.APP_VERSION, defaultConfig[ConfigKeys.APP_VERSION]),
  [ConfigKeys.PORTAL_URL]: getEnvVar(EnvKeys.PORTAL_URL, defaultConfig[ConfigKeys.PORTAL_URL]),
}

// Export configuration getter function
export function getConfig<T extends ConfigKeys>(key: T): AppConfig[T] {
  return config[key]
}

// Export entire config object (read-only)
export const appConfig: Readonly<AppConfig> = Object.freeze(config)

// Validate required environment variables on import
function validateConfig(): void {
  const requiredKeys = [EnvKeys.GOOGLE_CLIENT_ID]

  for (const key of requiredKeys) {
    if (!import.meta.env[key]) {
      console.error(`Missing required environment variable: ${key}`)
    }
  }
}

// Run validation
validateConfig()
